import React from 'react';
import PropTypes from 'prop-types';
import { intlShape } from '../../util/reactIntl';
import routeConfiguration from '../../routeConfiguration';
import {
  createSlug,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_NEW,
  LISTING_PAGE_PARAM_TYPES,
} from '../../util/urlHelpers';
import { ensureListing } from '../../util/data';
import { createResourceLocatorString } from '../../util/routes';
import {
  EditProjectListingBudgetPanel,
  EditProjectListingChatPanel,
  EditProjectListingDraftPanel,
} from '../../components';

import css from './EditProjectListingWizard.css';

export const CHAT = 'chat';
export const BUDGET = 'budget';
export const DRAFT = 'draft';

// EditListingWizardTab component supports these tabs
export const SUPPORTED_TABS = [CHAT, BUDGET, DRAFT];

const pathParamsToNextTab = (params, tab, marketplaceTabs) => {
  const nextTabIndex = marketplaceTabs.findIndex(s => s === tab) + 1;
  const nextTab =
    nextTabIndex < marketplaceTabs.length
      ? marketplaceTabs[nextTabIndex]
      : marketplaceTabs[marketplaceTabs.length - 1];
  return { ...params, tab: nextTab };
};

// When user has update draft listing, he should be redirected to next EditListingWizardTab
const redirectAfterDraftUpdate = (listingId, params, tab, marketplaceTabs, history) => {
  const currentPathParams = {
    ...params,
    type: LISTING_PAGE_PARAM_TYPE_DRAFT,
    id: listingId,
  };
  const routes = routeConfiguration();

  // Replace current "new" path to "draft" path.
  // Browser's back button should lead to editing current draft instead of creating a new one.
  if (params.type === LISTING_PAGE_PARAM_TYPE_NEW) {
    const draftURI = createResourceLocatorString(
      'EditProjectListingPage',
      routes,
      currentPathParams,
      {}
    );
    history.replace(draftURI);
  }

  // Redirect to next tab
  const nextPathParams = pathParamsToNextTab(currentPathParams, tab, marketplaceTabs);
  const to = createResourceLocatorString('EditProjectListingPage', routes, nextPathParams, {});
  history.push(to);
};

const EditProjectListingWizardTab = props => {
  const {
    tab,
    marketplaceTabs,
    params,
    errors,
    fetchInProgress,
    newListingPublished,
    history,
    listing,
    handleCreateFlowTabScrolling,
    handlePublishListing,
    onSaveAsDraft,
    onUpdateListing,
    onCreateListingDraft,
    onChange,
    updatedTab,
    updateInProgress,
    savingDraft,
    intl,
  } = props;

  const { type } = params;
  const isNewURI = type === LISTING_PAGE_PARAM_TYPE_NEW;
  const isDraftURI = type === LISTING_PAGE_PARAM_TYPE_DRAFT;
  const isNewListingFlow = isNewURI || isDraftURI;

  const currentListing = ensureListing(listing);
  const imageIds = images => {
    return images ? images.map(img => img.imageId || img.id) : null;
  };

  const onCompleteEditListingWizardTab = (tab, updateValues) => {
    // Normalize images for API call
    const { images: updatedImages, ...otherValues } = updateValues;
    const imageProperty =
      typeof updatedImages !== 'undefined' ? { images: imageIds(updatedImages) } : {};
    const updateValuesWithImages = { ...otherValues, ...imageProperty };

    if (isNewListingFlow) {
      const onUpsertListingDraft = isNewURI
        ? (tab, updateValues) => onCreateListingDraft(updateValues)
        : onUpdateListing;

      const upsertValues = isNewURI
        ? updateValuesWithImages
        : { ...updateValuesWithImages, id: currentListing.id };

      onUpsertListingDraft(tab, upsertValues)
        .then(r => {
          if (tab !== marketplaceTabs[marketplaceTabs.length - 1]) {
            // Create listing flow: smooth scrolling polyfill to scroll to correct tab
            handleCreateFlowTabScrolling(false);

            // After successful saving of draft data, user should be redirected to next tab
            redirectAfterDraftUpdate(r.data.data.id.uuid, params, tab, marketplaceTabs, history);
          } else {
            handlePublishListing(currentListing.id);
          }
        })
        .catch(e => {
          // No need for extra actions
        });
    } else {
      onUpdateListing(tab, { ...updateValuesWithImages, id: currentListing.id }).then(response => {
        // After saving edits to the published listing's main editor (DRAFT tab),
        // send the user back to the live listing so they can see the changes.
        // requestUpdateListing resolves with the SDK response on success and with
        // the (non-throwing) error action on failure, so guard on the response shape.
        const updatedId = response && response.data && response.data.data && response.data.data.id;
        if (tab === DRAFT && updatedId) {
          const slug = createSlug(updateValues.title || currentListing.attributes.title || '');
          const to = createResourceLocatorString(
            'ProjectListingPage',
            routeConfiguration(),
            { id: updatedId.uuid, slug },
            {}
          );
          history.push(to);
        }
      });
    }
  };

  const panelProps = tab => {
    return {
      className: css.panel,
      errors,
      listing,
      onChange,
      panelUpdated: updatedTab === tab,
      updateInProgress,
      saveDraftInProgress: savingDraft,
      // newListingPublished and fetchInProgress are flags for the last wizard tab
      ready: newListingPublished,
      disabled: fetchInProgress,
      intl,
    };
  };

  switch (tab) {
    case CHAT: {
      const submitButtonTranslationKey = isNewListingFlow
        ? 'EditProjectListingWizard.saveNewChat'
        : 'EditProjectListingWizard.saveEditChat';
      return (
        <EditProjectListingChatPanel
          {...panelProps(CHAT)}
          inlineBudget={isNewListingFlow}
          submitButtonText={intl.formatMessage({ id: submitButtonTranslationKey })}
          onSubmit={values => {
            onCompleteEditListingWizardTab(tab, values);
          }}
        />
      );
    }
    case BUDGET: {
      const submitButtonTranslationKey = isNewListingFlow
        ? 'EditProjectListingWizard.saveNewBudget'
        : 'EditProjectListingWizard.saveEditBudget';
      return (
        <EditProjectListingBudgetPanel
          {...panelProps(BUDGET)}
          submitButtonText={intl.formatMessage({ id: submitButtonTranslationKey })}
          onSubmit={values => {
            onCompleteEditListingWizardTab(tab, values);
          }}
        />
      );
    }
    case DRAFT: {
      const submitButtonTranslationKey = isNewListingFlow
        ? 'EditProjectListingWizard.saveNewDraft'
        : 'EditProjectListingWizard.saveEditDraft';
      // "Save as draft" (persist without publishing, then exit) is only offered in
      // the new-listing flow, where the primary action is publishing. Edit mode
      // keeps its single save button. The manual-fill form's link to the AI chat
      // is new-flow only too: edit mode's chat tab has no inline budget step.
      const saveDraftProps = isNewListingFlow
        ? {
            saveDraftActionMsg: intl.formatMessage({ id: 'EditProjectListingWizard.saveAsDraft' }),
            onSaveDraft: values => onSaveAsDraft({ ...values, id: currentListing.id }),
            aiChatLinkParams: { ...params, tab: CHAT },
          }
        : {};
      return (
        <EditProjectListingDraftPanel
          {...panelProps(DRAFT)}
          {...saveDraftProps}
          submitButtonText={intl.formatMessage({ id: submitButtonTranslationKey })}
          onSubmit={values => {
            onCompleteEditListingWizardTab(tab, values);
          }}
        />
      );
    }
    default:
      return null;
  }
};

EditProjectListingWizardTab.defaultProps = {
  listing: null,
  updatedTab: null,
  savingDraft: false,
};

const { array, bool, func, object, oneOf, shape, string } = PropTypes;

EditProjectListingWizardTab.propTypes = {
  params: shape({
    id: string.isRequired,
    slug: string.isRequired,
    type: oneOf(LISTING_PAGE_PARAM_TYPES).isRequired,
    tab: oneOf(SUPPORTED_TABS).isRequired,
  }).isRequired,
  errors: shape({
    createListingDraftError: object,
    publishListingError: object,
    updateListingError: object,
    showListingsError: object,
    uploadImageError: object,
    saveListingDraftError: object,
  }).isRequired,
  fetchInProgress: bool.isRequired,
  newListingPublished: bool.isRequired,
  history: shape({
    push: func.isRequired,
    replace: func.isRequired,
  }).isRequired,
  images: array.isRequired,
  availability: object.isRequired,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: shape({
    attributes: shape({
      publicData: object,
      description: string,
      geolocation: object,
      pricing: object,
      title: string,
    }),
    images: array,
  }),

  handleCreateFlowTabScrolling: func.isRequired,
  handlePublishListing: func.isRequired,
  onSaveAsDraft: func.isRequired,
  savingDraft: bool,
  onUpdateListing: func.isRequired,
  onCreateListingDraft: func.isRequired,
  onImageUpload: func.isRequired,
  onUpdateImageOrder: func.isRequired,
  onRemoveImage: func.isRequired,
  onChange: func.isRequired,
  updatedTab: string,
  updateInProgress: bool.isRequired,

  intl: intlShape.isRequired,
};

export default EditProjectListingWizardTab;
